import { useEffect, useState } from "react";
import { TreeNode } from "../../../../../components/TreeCheckboxGroup";
import { prepareDataForTreeCheckboxGroup } from "./utils";
import { useModuleIds, useVisibleData } from "../../../../../hooks/useData";
import { useIntl } from "react-intl";
import { useTutos } from "../../../Tuto/useTutos";
import { LearningSetTypes } from "../../../../../interfaces/AthenaResources";
import { useWorkshops } from "../../../Workshop/useWorkshops";
import {
    FeatureFlags,
    useFeatureFlags,
} from "@evidenceb/athena-common/modules/FeatureFlags";
import { workmodesMessages } from "../../../workmodesMessages";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";
import { useExercises } from "../../../../../hooks/queries/useExercisesQuery";

export type PlaylistableResources = Extract<
    LearningSetTypes,
    | LearningSetTypes.Module
    | LearningSetTypes.Tutorial
    | LearningSetTypes.Workshop
>;

const usePlaylistableResources = () => {
    const intl = useIntl();
    const flags = useFeatureFlags();

    const [resources, setResources] = useState<Record<string, TreeNode>>();
    const [initialized, setInitialized] = useState<{
        [key in PlaylistableResources]: boolean;
    }>(initialInitStatus(flags));

    const data = useVisibleData();
    const exercises = useExercises();
    const moduleIds = useModuleIds();
    const { queries: tutosQueries, data: tutosData } = useTutos({
        enabled: flags.tutosInPlaylist,
    });
    const { queries: workshopQueries, data: workshopData } = useWorkshops({
        enabled: flags.workshopsInPlaylist,
    });

    const modulesOnly = showOnlyModules(flags);

    useEffect(() => {
        let abort = false;
        setTimeout(() => {
            const resources = prepareDataForTreeCheckboxGroup(
                data,
                exercises,
                flags.tutosInPlaylist || flags.workshopsInPlaylist,
                intl
            );
            if (!abort) {
                setResources((curr) => ({
                    ...(curr ?? {}),
                    [LearningSetTypes.Module]: {
                        id: LearningSetTypes.Module,
                        label: intl.formatMessage(
                            workmodesMessages[WorkModeType.SoloAI]
                        ),
                        childrenIds: data.modules.map(({ id }) => id),
                        readOnly: true,
                    },
                    ...resources,
                }));
                setInitialized((curr) => ({
                    ...curr,
                    [LearningSetTypes.Module]: true,
                }));
            }
        }, 0);
        return () => {
            abort = true;
        };
    }, [intl, data, flags, exercises]);

    useEffect(() => {
        if (
            initialized[LearningSetTypes.Tutorial] ||
            tutosQueries.some((query) => query.isLoading)
        )
            return;
        if (tutosData.length === 0) {
            setInitialized((curr) => ({
                ...curr,
                [LearningSetTypes.Tutorial]: true,
            }));
            return;
        }
        const tutosNodes = tutosData.reduce<Record<string, TreeNode>>(
            (obj, tuto) => ({
                ...obj,
                [tuto.id]: {
                    id: tuto.id,
                    label: tuto.name,
                    idPrefix: [tuto.collectionId],
                    idSuffix: [tuto.learning_items[0]],
                },
            }),
            {}
        );

        const tutosParentNode: Record<string, TreeNode> = {
            [LearningSetTypes.Tutorial]: {
                id: LearningSetTypes.Tutorial,
                label: intl.formatMessage(workmodesMessages[WorkModeType.Tuto]),
                childrenIds: tutosData.map(({ id }) => id),
                readOnly: true,
            },
        };
        setResources((curr) => ({
            ...(curr ?? {}),
            ...tutosNodes,
            ...tutosParentNode,
        }));
        setInitialized((curr) => ({
            ...curr,
            [LearningSetTypes.Tutorial]: true,
        }));
    }, [tutosData, tutosQueries, initialized, intl]);

    useEffect(() => {
        if (
            initialized[LearningSetTypes.Workshop] ||
            workshopQueries.some((query) => query.isLoading)
        )
            return;
        if (workshopData.length === 0) {
            setInitialized((curr) => ({
                ...curr,
                [LearningSetTypes.Workshop]: true,
            }));
            return;
        }
        const workshopNodes = workshopData.reduce<Record<string, TreeNode>>(
            (obj, workshop) => ({
                ...obj,
                [workshop.id]: {
                    id: workshop.id,
                    label: workshop.name,
                    idPrefix: [workshop.collectionId],
                    idSuffix: [workshop.learning_items[0]],
                },
            }),
            {}
        );
        const workshopParentNode: Record<string, TreeNode> = {
            [LearningSetTypes.Workshop]: {
                id: LearningSetTypes.Workshop,
                label: intl.formatMessage(
                    workmodesMessages[WorkModeType.Workshop]
                ),
                childrenIds: workshopData.map(({ id }) => id),
                readOnly: true,
            },
        };
        setResources((curr) => ({
            ...(curr ?? {}),
            ...workshopNodes,
            ...workshopParentNode,
        }));
        setInitialized((curr) => ({
            ...curr,
            [LearningSetTypes.Workshop]: true,
        }));
    }, [workshopData, workshopQueries, initialized, intl]);

    return {
        resources: Object.values(initialized).every((value) => value)
            ? resources
            : undefined,
        entryIds: modulesOnly ? moduleIds : resourceTypes,
    };
};
export default usePlaylistableResources;

const resourceTypes = [
    LearningSetTypes.Module,
    LearningSetTypes.Tutorial,
    LearningSetTypes.Workshop,
];

const initialInitStatus = (
    flags: FeatureFlags
): { [key in PlaylistableResources]: boolean } => ({
    [LearningSetTypes.Module]: false,
    [LearningSetTypes.Tutorial]: flags.tutosInPlaylist ? false : true,
    [LearningSetTypes.Workshop]: flags.workshopsInPlaylist ? false : true,
});

const showOnlyModules = (flags: FeatureFlags) => {
    return !flags.tutosInPlaylist && !flags.workshopsInPlaylist;
};
